import { fintheonConversationStorageKey } from "./lib/fintheonAgentRouting";
import { playVoiceSidebarHandoffSound } from "./utils/soundAlerts";
import type { ResponseScenario } from "./voice-cao-dock-data";

interface VoiceCaoSidebarContext {
  kind: string;
  label: string;
  detail: string;
}

interface VoiceCaoSidebarHandoffInput {
  response: ResponseScenario;
  context: VoiceCaoSidebarContext | null;
  conversationId: string | null | undefined;
  lastUserText: string;
  lastAssistantText: string;
}

export function openVoiceCaoSidebarHandoff(
  input: VoiceCaoSidebarHandoffInput,
): void {
  if (typeof window === "undefined") return;

  const message = buildVoiceCaoSidebarHandoff(input);
  window.dispatchEvent(new Event("fintheon:open-chat-panel"));

  if (input.conversationId) {
    persistSidebarConversationId(input.conversationId);
    window.dispatchEvent(
      new CustomEvent("fintheon:chat-load-session", {
        detail: { id: input.conversationId },
      }),
    );
  }

  window.setTimeout(
    () => {
      window.dispatchEvent(
        new CustomEvent("fintheon:chat-sidebar-send", {
          detail: { message },
        }),
      );
      playVoiceSidebarHandoffSound();
    },
    input.conversationId ? 220 : 80,
  );
}

export function buildVoiceCaoSidebarHandoff(
  input: VoiceCaoSidebarHandoffInput,
): string {
  const lines = [
    "[Voice CAO handoff]",
    `Dock state: ${input.response.phrase}`,
    `Thinking stream: ${input.response.stream}`,
    input.context
      ? `Source context: ${input.context.label} (${input.context.detail})`
      : null,
    input.lastUserText
      ? `User request: ${trimForSidebar(input.lastUserText)}`
      : null,
    input.lastAssistantText
      ? `Latest voice reply: ${trimForSidebar(input.lastAssistantText)}`
      : null,
    summarizeResponse(input.response),
    summarizeTools(input.response),
    "Continue from this context in sidebar CAO chat.",
  ];

  return lines.filter(Boolean).join("\n");
}

function persistSidebarConversationId(conversationId: string): void {
  try {
    window.localStorage.setItem(
      fintheonConversationStorageKey("harper", "chat"),
      conversationId,
    );
  } catch {
    /* ignore */
  }
}

function summarizeResponse(response: ResponseScenario): string | null {
  if (response.kind === "trade" && response.tradeIdea) {
    return [
      `Trade idea: ${response.tradeIdea.label}`,
      `Entry ${response.tradeIdea.entry}`,
      `SL ${response.tradeIdea.stopLoss}`,
      `TP ${response.tradeIdea.takeProfit}`,
      `CAO confidence ${response.tradeIdea.confidenceScore.toFixed(1)}`,
    ].join(" | ");
  }
  if (response.kind === "arbitrum" && response.linkCta) {
    return `Arbitrum handoff: ${response.linkCta.label}`;
  }
  if (response.kind === "riskflow" && response.riskflowHeadline) {
    return `RiskFlow headline: ${response.riskflowHeadline.headline}`;
  }
  if (response.kind === "open-chat" && response.openChat) {
    return `Rich reply handoff: ${response.openChat.detail}`;
  }
  return null;
}

function summarizeTools(response: ResponseScenario): string | null {
  if (response.tools.length === 0) return null;
  const tools = response.tools
    .map((tool) => `${tool.name}: ${tool.action}`)
    .join("; ");
  return `Connector calls: ${tools}`;
}

function trimForSidebar(value: string): string {
  const normalized = value.replace(/\s+/g, " ").trim();
  if (normalized.length <= 520) return normalized;
  return `${normalized.slice(0, 517)}...`;
}
